import { Capacitor, registerPlugin } from "@capacitor/core";
import { parseBankSms, smsKey } from "./parseSms";

// The plugin is written in Java (android/.../SmsInboxPlugin.java). It only exists in the
// Android app; there is no equivalent on iPhone, which does not let apps read messages.
const SmsInbox = registerPlugin("SmsInbox");

const IMPORTED_KEY = "budgetsprint-sms-imported";
const SKIPPED_KEY = "budgetsprint-sms-skipped";
// When this phone last read the inbox (ms), and whether it does so by itself when the
// app opens (on unless switched off).
const LAST_SCAN_KEY = "budgetsprint-sms-last-scan";
const AUTO_KEY = "budgetsprint-sms-auto";
const DAY = 86400000;

export const autoSmsEnabled = () => {
  try {
    return localStorage.getItem(AUTO_KEY) !== "off";
  } catch {
    return false;
  }
};
export const setAutoSms = (on) => {
  try {
    localStorage.setItem(AUTO_KEY, on ? "on" : "off");
  } catch {
    // ignore
  }
};

export const lastScanAt = () => {
  try {
    return Number(localStorage.getItem(LAST_SCAN_KEY)) || null;
  } catch {
    return null;
  }
};
export const rememberScan = (ms = Date.now()) => {
  try {
    localStorage.setItem(LAST_SCAN_KEY, String(ms));
  } catch {
    // ignore
  }
};

// How many days back to read: since this phone last read the inbox; the first time,
// since the newest entry in the app (`latestIso`), or 30 days with none. Between 1 and 90.
export const daysToScan = (latestIso, now = Date.now()) => {
  const last = lastScanAt();
  const since = last || (latestIso ? new Date(`${latestIso}T00:00:00`).getTime() : now - 30 * DAY);
  return Math.min(90, Math.max(1, Math.ceil((now - since) / DAY) + 1));
};

export const smsSupported = () => Capacitor.getPlatform() === "android";

export const smsPermission = async () => (await SmsInbox.checkPermissions()).sms;

export const requestSmsPermission = async () => (await SmsInbox.requestPermissions()).sms;

const readSet = (name) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(name) || "[]"));
  } catch {
    return new Set();
  }
};

const writeSet = (name, set) => {
  try {
    // Only the newest few thousand fingerprints matter; older messages age out of the scan window.
    localStorage.setItem(name, JSON.stringify([...set].slice(-3000)));
  } catch {
    // If storage is unavailable the server still refuses duplicates.
  }
};

export const rememberImported = (keys) => {
  const set = readSet(IMPORTED_KEY);
  keys.forEach((k) => set.add(k));
  writeSet(IMPORTED_KEY, set);
};

export const rememberSkipped = (keys) => {
  const set = readSet(SKIPPED_KEY);
  keys.forEach((k) => set.add(k));
  writeSet(SKIPPED_KEY, set);
};

// Reads the inbox on the phone. `fresh` is the messages that look like transactions or
// bills, minus ones already imported or skipped; `all` also keeps those, so repeating
// payments can be spotted across the whole period. `read` is how many messages were
// looked at, `imported` / `skipped` how many of the useful ones were handled before
// (`skippedEntries` are those, to offer them again). Nothing leaves the phone here.
// `savedKeys` are the fingerprints on entries that exist in the app now; only those count
// as already added, so a message whose entry was deleted (or reset) is offered again.
export const scanInbox = async (days = 30, savedKeys = null) => {
  const { messages } = await SmsInbox.list({ days, limit: 2000 });
  const remembered = readSet(IMPORTED_KEY);
  // A "due soon" notice for a bill already on the list makes no entry of its own, so
  // for those this phone's memory is all there is.
  const imported = savedKeys ? { has: (key, kind) => savedKeys.has(key) || (kind === "bill" && remembered.has(key)) } : remembered;
  const skipped = readSet(SKIPPED_KEY);

  const fresh = [];
  const all = [];
  const skippedEntries = [];
  let importedCount = 0;
  for (const message of messages) {
    const parsed = parseBankSms(message);
    if (!parsed) continue;
    const key = await smsKey(message);
    const entry = { ...parsed, key };
    all.push(entry);
    if (imported.has(key, entry.kind)) importedCount += 1;
    else if (skipped.has(key)) skippedEntries.push(entry);
    else fresh.push(entry);
  }
  return { fresh, all, read: messages.length, imported: importedCount, skipped: skippedEntries.length, skippedEntries };
};

// Everything this phone remembers about SMS (imported, skipped, when it last looked,
// questions turned down), for when the data is reset or the account deleted.
export const clearSmsMemory = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("budgetsprint-sms-") || k === "budgetsprint-recurring-dismissed")
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
};

// Skipped messages brought back, so they can be reviewed again.
export const forgetSkipped = (keys) => {
  const set = readSet(SKIPPED_KEY);
  keys.forEach((k) => set.delete(k));
  writeSet(SKIPPED_KEY, set);
};
